import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

export default function Encaissements() {
  const [rows, setRows] = useState([]);
  const [sales, setSales] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sale_id: "", amount: "", account_id: "", date: todayISO(), reference: "" });

  const load = () => api.get("/encaissements").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/sales").then((r) => setSales(r.data.filter((s) => s.remaining > 0)));
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, []);

  const total = rows.reduce((s, t) => s + (t.amount || 0), 0);

  const submit = async () => {
    if (!form.sale_id || !form.amount) return toast.error("Vente et montant requis");
    try {
      await api.post(`/sales/${form.sale_id}/payments`, { amount: Number(form.amount), account_id: form.account_id || null, date: form.date });
      toast.success("Encaissement enregistré — solde client mis à jour");
      setOpen(false);
      setForm({ sale_id: "", amount: "", account_id: "", date: todayISO(), reference: "" });
      load();
      api.get("/sales").then((r) => setSales(r.data.filter((s) => s.remaining > 0)));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="encaissements-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Encaissements</h1>
          <p className="mt-1 text-sm text-slate-500">Total encaissé : <span className="font-mono-num font-bold text-emerald-600">{fmtMoney(total)}</span> — chaque paiement réduit automatiquement le solde du client.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-encaissement-button"><Plus className="mr-1 h-4 w-4" /> Nouvel encaissement</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Enregistrer un encaissement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Vente avec solde restant *</Label>
                <Select value={form.sale_id} onValueChange={(v) => {
                  const s = sales.find((x) => x.id === v);
                  setForm({ ...form, sale_id: v, amount: s ? String(s.remaining) : "" });
                }}>
                  <SelectTrigger data-testid="encaissement-sale-select"><SelectValue placeholder="Choisir une vente…" /></SelectTrigger>
                  <SelectContent>{sales.map((s) => <SelectItem key={s.id} value={s.id}>{s.sale_number} — {s.customer_name} · reste {fmtMoney(s.remaining)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Montant (FCFA)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="encaissement-amount" /></div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Compte destination</Label>
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="encaissement-account"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="encaissement-submit">Valider l'encaissement</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="encaissements-table" rows={rows}
        searchKeys={["description", "reference", "customer_name", "doc_number"]}
        columns={[
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "customer_name", label: "Client", render: (r) => r.customer_name || "—" },
          { key: "doc_number", label: "Vente", render: (r) => <span className="font-mono-num font-semibold">{r.doc_number || r.reference || "—"}</span> },
          { key: "vehicle_label", label: "Véhicule", render: (r) => r.vehicle_label || "—" },
          { key: "description", label: "Description", render: (r) => <span className="text-slate-500">{r.description}</span> },
          { key: "account_name", label: "Compte" },
          { key: "amount", label: "Montant", className: "text-right", render: (r) => <span className="font-mono-num font-bold text-emerald-600">+{fmtMoney(r.amount)}</span> },
          { key: "user_name", label: "Par", render: (r) => <span className="text-xs text-slate-500">{r.user_name}</span> },
        ]}
      />
    </div>
  );
}
